import React from "react";
import { useTranslation } from "react-i18next";
import AdnPageTitle from "../common/adnPageTitle/AdnPageTitle";
import AdnTable from "../common/adnTable/AdnTable";
import AdnCard from "../common/adnCard/AdnCard";
import AdnAddButton from "../common/adnAddButton/AdnAddButton";
import { Col, Row, Space, Popconfirm } from "antd";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { AppRoutes } from "../../AppRoutes";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AdnAutoComplete from "../common/adnAutoComplete/AdnAutoComplete";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import {
  MonthsPortuguese,
  QuizStatus,
  QuizStatusOptions,
} from "../common/GeneralConfigurations";
import "./QuizListForm.css";
import moment from "moment";
import Icon from "@ant-design/icons/lib/components/Icon";
import { ReactComponent as fluencyPT } from "../../assets/images/image_languages/icons8-portugal-48.svg";
import { ReactComponent as fluencyEN } from "../../assets/images/image_languages/icons8-great-britain-48.svg";
import { Eye } from "react-feather";

const QuizListForm = ({
  loading,
  quizList,
  addQuiz,
  handleDelete,
  handleChangeQuizStatus,
  quizBuilderListStatus,
  handleQuizTypeChange,
  quizType,
  handleQuizReferenceChange,
  quizReference,
  saveLocalVariables,
  handleOrderList,
  totalRecords,
  handleChangePage,
  currentPage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const title = t("quizList.form.title");
  const columns = [
    {
      title: t("quizList.form.columns.quizType"),
      dataIndex: "quizTypeId",
      key: "quizTypeId",
      sorter: true,
      render: (text, record) => (
        <Link to={AppRoutes.quizBuilderById.createPath(record.id)}>
          {saveLocalVariables()}
          {record.followUpEventDescription}
        </Link>
      ),
    },
    {
      title: t("quizList.form.columns.contractType"),
      dataIndex: "contractTypeId",
      key: "contractTypeId",
      render: (text, record) =>
        record.contractTypeDescription !== null &&
        record.contractTypeDescription,
    },
    {
      title: t("quizList.form.columns.language"),
      dataIndex: "language",
      key: "language",
      render: (record) =>
        record === "EN" ? (
          <Row align="middle">
            <Icon
              className="quizBuilderListLanguage"
              component={fluencyEN}
              style={{ marginRight: "4px" }}
            />
            EN
          </Row>
        ) : (
          <Row align="middle">
            <Icon
              className="quizBuilderListLanguage"
              component={fluencyPT}
              style={{ marginRight: "4px" }}
            />
            PT
          </Row>
        ),
    },
    {
      title: t("quizList.form.columns.reference"),
      dataIndex: "reference",
      key: "reference",
      sorter: true,
    },
    {
      title: t("quizList.form.columns.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) =>
        text
          ? moment(text).format("DD") +
            "  " +
            MonthsPortuguese[parseInt(moment(text).format("MM"))] +
            " " +
            moment(text).format("YYYY")
          : undefined,
    },
    {
      title: t("quizList.form.columns.status"),
      dataIndex: "status",
      key: "status",
      render(text, record) {
        return {
          children:
            record.status === QuizStatus.Draft ? (
              <div className="state-tag tag-to-do">
                {t("quizList.form.status.draft")}
              </div>
            ) : record.status === QuizStatus.Published ? (
              <div className="state-tag tag-done">
                {t("quizList.form.status.published")}
              </div>
            ) : (
              <div className="state-tag tag-canceled">
                {t("quizList.form.status.unpublished")}
              </div>
            ),
        };
      },
    },
    {
      title: t("quizList.form.columns.actions"),
      key: "actions",
      render: (record) => (
        <Space>
          {record.status === 0 ? (
            <EditOutlined
              style={{ color: "black", fontSize: "medium" }}
              onClick={() => {
                saveLocalVariables();
                navigate(AppRoutes.quizBuilderById.createPath(record.id));
              }}
            />
          ) : (
            <Eye
              width={16}
              height={16}
              style={{ color: "black" }}
              onClick={() => {
                saveLocalVariables();
                navigate(AppRoutes.quizBuilderById.createPath(record.id));
              }}
            />
          )}
          {record.status === 0 && (
            <Popconfirm
              title={t("quizList.form.confirmations.deleteConfirmation")}
              onConfirm={() => handleDelete(record.id)}
            >
              <CloseOutlined
                className="quizListDeleteButton"
                style={{ color: "red" }}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <AdnPageTitle title={title} />
      <AdnCard loading={loading}>
        <Row gutter={16} justify="space-between" align="middle">
          <Col>
            <AdnAddButton
              style={{ float: "right" }}
              name={t("quizList.form.buttons.quiz")}
              handleClick={() => addQuiz(AppRoutes.quizBuilder)}
            />
          </Col>

          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 24 }}
            xl={{ span: 20 }}
            xxl={{ span: 20 }}
          >
            <Row gutter={16} style={{ marginTop: "1%" }}>
              <Col
                className="filter-item"
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 24 }}
                lg={{ span: 24 }}
                xl={{ span: 12 }}
                xxl={{ span: 12 }}
              >
                <AdnAutoComplete
                  name={"quizType"}
                  placeholder={t("quizList.form.placeholders.searchByQuizType")}
                  handleSearch={(value) => handleQuizTypeChange(value)}
                  resultList={[]}
                  defaultValue={quizType}
                  allowClear={true}
                  valueId={"quizType"}
                  maxLength={100}
                />
              </Col>
              <Col
                className="quizListReferenceFilter filter-item"
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 12 }}
                lg={{ span: 12 }}
                xl={{ span: 6 }}
                xxl={{ span: 6 }}
              >
                <AdnAutoComplete
                  name={"quizReference"}
                  placeholder={t(
                    "quizList.form.placeholders.searchByReference"
                  )}
                  handleSearch={(value) => handleQuizReferenceChange(value)}
                  resultList={[]}
                  defaultValue={quizReference}
                  allowClear={true}
                  valueId={"quizReference"}
                  maxLength={20}
                />
              </Col>
              <Col
                className="quizListStatusFilter filter-item"
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 12 }}
                lg={{ span: 12 }}
                xl={{ span: 6 }}
                xxl={{ span: 6 }}
              >
                <AdnDropdown
                  name={"quizBuilderListStatus"}
                  placeholder={t("quizList.form.placeholders.searchByStatus")}
                  handleChange={(value) => handleChangeQuizStatus(value)}
                  allowClear={true}
                  valueId={"quizBuilderListStatus"}
                  defaultValue={quizBuilderListStatus}
                  options={QuizStatusOptions}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <AdnTable
              columns={columns}
              data={quizList}
              handleChange={handleOrderList}
              totalRecords={totalRecords}
              currentPage={currentPage}
              handleChangePage={handleChangePage}
            />
          </Col>
        </Row>
      </AdnCard>
    </>
  );
};

export default QuizListForm;
